/**
 * Registry of dedicated Qawaem agents — one per ERP module.
 * Single source for sidebar links, routes, labels, and agent config.
 */

import { getWorkspaceTabsForModule } from './agentWorkspaceTabs';
import { getModuleQuickChips } from './agentModuleQuickChips';
import { mp, buildPromptAddon } from './agentModuleI18n';

const DEFAULT_VECTORIZE = ['client', 'product', 'supplier', 'service', 'category'];

/** @type {import('./agentModuleTypes').AgentModuleDefinition[]} */
export const AGENT_MODULE_DEFINITIONS = [
  {
    id: 'facturation',
    moduleKey: 'FACTURATION',
    agentPath: '/facturation/agent',
    routePrefixes: [
      '/dashboard',
      '/document-creation',
      '/facturation',
      '/archives',
      '/payments',
      '/f/',
    ],
    vectorizeTypes: DEFAULT_VECTORIZE,
  },
  {
    id: 'inventaire',
    moduleKey: 'INVENTAIRE',
    agentPath: '/inventaire/agent',
    routePrefixes: ['/inventaire', '/bord-inventaire', '/warehouses', '/valorisation', '/mouvements-stock', '/transferts', '/ajustements'],
  },
  {
    id: 'article',
    moduleKey: 'ARTICLE',
    agentPath: '/article/agent',
    routePrefixes: ['/products', '/services', '/categories', '/articles'],
    vectorizeTypes: ['product', 'service', 'category', 'supplier'],
  },
  {
    id: 'achats',
    moduleKey: 'ACHATS',
    agentPath: '/achats/agent',
    routePrefixes: ['/achats'],
  },
  {
    id: 'ventes',
    moduleKey: 'VENTES',
    agentPath: '/ventes/agent',
    routePrefixes: ['/ventes', '/commercial'],
  },
  {
    id: 'rh',
    moduleKey: 'RESSOURCES_HUMAINES',
    agentPath: '/rh/agent',
    routePrefixes: ['/hr', '/employees', '/departments'],
  },
  {
    id: 'pointage',
    moduleKey: 'POINTAGE',
    agentPath: '/pointage/agent',
    routePrefixes: ['/timetracking', '/pointage'],
  },
  {
    id: 'salaires',
    moduleKey: 'SALAIRES',
    agentPath: '/salaires/agent',
    routePrefixes: ['/salaries'],
  },
  {
    id: 'situation',
    moduleKey: 'GESTION_SITUATION',
    agentPath: '/situation/agent',
    routePrefixes: ['/gestion-situation', '/situation'],
  },
  {
    id: 'caisse',
    moduleKey: 'CAISSE',
    agentPath: '/caisse/agent',
    routePrefixes: ['/treasury', '/caisse'],
  },
  {
    id: 'banque',
    moduleKey: 'BANQUE',
    agentPath: '/banque/agent',
    routePrefixes: ['/banking', '/banque'],
  },
  {
    id: 'comptabilite',
    moduleKey: 'COMPTABILITE',
    agentPath: '/comptabilite/agent',
    routePrefixes: ['/comptabilite', '/situation'],
  },
  {
    id: 'tresorerie',
    moduleKey: 'TRESORERIE',
    agentPath: '/tresorerie/agent',
    routePrefixes: ['/tresorerie'],
  },
  {
    id: 'budget',
    moduleKey: 'BUDGET',
    agentPath: '/budget/agent',
    routePrefixes: ['/budget', '/profit'],
  },
  {
    id: 'projets',
    moduleKey: 'PROJETS',
    agentPath: '/projets/agent',
    routePrefixes: ['/projects', '/projets'],
  },
  {
    id: 'contrats',
    moduleKey: 'CONTRATS',
    agentPath: '/contrats/agent',
    routePrefixes: ['/contracts', '/contrats'],
  },
  {
    id: 'production',
    moduleKey: 'PRODUCTION',
    agentPath: '/production/agent',
    routePrefixes: ['/production', '/production-dashboard', '/production-planning', '/atelier-qualite', '/technical-data', '/workstation-tracking', '/operators-management', '/production-reports'],
  },
  {
    id: 'maintenance',
    moduleKey: 'MAINTENANCE',
    agentPath: '/maintenance/agent',
    routePrefixes: ['/maintenance'],
  },
  {
    id: 'qualite',
    moduleKey: 'QUALITE',
    agentPath: '/qualite/agent',
    routePrefixes: ['/qualite'],
  },
  {
    id: 'import_export',
    moduleKey: 'IMPORT_EXPORT',
    agentPath: '/import-export/agent',
    routePrefixes: ['/import-export'],
  },
  {
    id: 'parc_auto',
    moduleKey: 'PARC_AUTO',
    agentPath: '/parc-auto/agent',
    routePrefixes: ['/fleet', '/parc-auto'],
  },
  {
    id: 'logistique',
    moduleKey: 'LOGISTIQUE',
    agentPath: '/logistique/agent',
    routePrefixes: ['/logistique'],
  },
  {
    id: 'distribution',
    moduleKey: 'DISTRIBUTION',
    agentPath: '/distribution/agent',
    routePrefixes: ['/distribution'],
  },
  {
    id: 'livraison',
    moduleKey: 'LIVRAISON',
    agentPath: '/livraison/agent',
    routePrefixes: ['/livraison'],
  },
  {
    id: 'point_de_vente',
    moduleKey: 'POINT_DE_VENTE',
    agentPath: '/point-de-vente/agent',
    routePrefixes: ['/pos', '/point-de-vente'],
  },
  {
    id: 'marketing',
    moduleKey: 'MARKETING',
    agentPath: '/marketing/agent',
    routePrefixes: ['/marketing'],
  },
  {
    id: 'crm',
    moduleKey: 'CRM',
    agentPath: '/crm/agent',
    routePrefixes: ['/crm', '/relation-client'],
  },
  {
    id: 'relation_fournisseur',
    moduleKey: 'RELATION_FOURNISSEUR',
    agentPath: '/relation-fournisseur/agent',
    routePrefixes: ['/relation-fournisseur'],
    vectorizeTypes: ['supplier'],
  },
  {
    id: 'service_client',
    moduleKey: 'SERVICE_CLIENT',
    agentPath: '/service-client/agent',
    routePrefixes: ['/service-client'],
  },
  {
    id: 'taches',
    moduleKey: 'TACHES',
    agentPath: '/taches/agent',
    routePrefixes: ['/taches'],
  },
  {
    id: 'approbations',
    moduleKey: 'APPROBATIONS',
    agentPath: '/approbations/agent',
    routePrefixes: ['/approbations'],
  },
  {
    id: 'planification',
    moduleKey: 'PLANIFICATION',
    agentPath: '/planification/agent',
    routePrefixes: ['/planification'],
  },
];

export function buildAgentModuleConfig(def) {
  const shortLabel = mp(def.id, 'shortLabel');
  const quickChips = getModuleQuickChips(def.id);

  return {
    id: def.id,
    moduleKey: def.moduleKey,
    label: mp(def.id, 'label'),
    shortLabel,
    agentPath: def.agentPath,
    subtitle: mp(def.id, 'subtitle'),
    routePrefixes: [...(def.routePrefixes || []), def.agentPath.replace('/agent', '')],
    placeholder: mp(def.id, 'placeholder'),
    promptAddon: buildPromptAddon(def.id),
    quickChips,
    workspaceTabs: getWorkspaceTabsForModule(def.id),
    vectorizeTypes: def.vectorizeTypes || DEFAULT_VECTORIZE,
    emptyStateActions: quickChips.slice(0, 4).map((c) => ({
      title: c.label,
      desc: shortLabel,
      prompt: c.prompt,
    })),
  };
}

export const AGENT_MODULES = AGENT_MODULE_DEFINITIONS.map(buildAgentModuleConfig);

export const AGENT_MODULE_BY_ID = Object.fromEntries(
  AGENT_MODULES.map((mod) => [mod.id, mod]),
);

export const AGENT_MODULE_BY_PATH = Object.fromEntries(
  AGENT_MODULES.map((mod) => [mod.agentPath, mod]),
);

export const AGENT_MODULE_BY_KEY = Object.fromEntries(
  AGENT_MODULES.map((mod) => [mod.moduleKey, mod]),
);

export const AGENT_MODULE_ROUTES = AGENT_MODULE_DEFINITIONS.map((def) => ({
  path: def.agentPath,
  featureRoute: def.agentPath,
  moduleId: def.id,
}));

export function isAgentWorkspacePath(pathname) {
  return Boolean(AGENT_MODULE_BY_PATH[pathname]);
}

export function resolveAgentModuleFromAgentPath(pathname) {
  return AGENT_MODULE_BY_PATH[pathname] || null;
}

export function getAgentRoutesForLicense() {
  return Object.fromEntries(
    AGENT_MODULE_DEFINITIONS.map((def) => [def.moduleKey, def.agentPath]),
  );
}
